import { Branch } from "../gen/clients/llts";

/**
 * Branch id sentinel that represents "no branch context" - paths for these users
 * must not contain a branch segment.
 */
const DEFAULT_BRANCH_ID = "default";

/**
 * Returns true when the supplied branch represents the default branch
 * (i.e. the user should browse pages without a branch URL prefix).
 * `null`, `undefined`, missing id and the literal id "default" are all treated the same.
 */
export const isDefaultBranch = (branch: Branch | null | undefined): boolean => {
  if (!branch || !branch.id) {
    return true;
  }
  return branch.id.toLowerCase() === DEFAULT_BRANCH_ID;
};

/**
 * Removes the leading "/{branchId}" segment from the pathname. The match is performed
 * against the exact branchId text taken from the URL (typically obtained via
 * react-router's useParams), so casing matches what the user typed.
 * Returns "/" when stripping leaves an empty path.
 */
export const stripBranchSegment = (pathname: string, branchId: string | undefined): string => {
  if (!branchId) {
    return pathname || "/";
  }
  const prefix = `/${branchId}`;
  if (pathname === prefix) {
    return "/";
  }
  if (pathname.startsWith(`${prefix}/`)) {
    return pathname.slice(prefix.length);
  }
  return pathname;
};

/**
 * Prepends the canonical branch id to a branch-less pathname. The pathname is expected
 * to start with "/" and not already contain a branch segment.
 */
export const prependBranchSegment = (pathname: string, branchId: string): string => {
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (normalizedPath === "/") {
    return `/${branchId}`;
  }
  return `/${branchId}${normalizedPath}`;
};

interface ComputeArgs {
  pathname: string;
  search: string;
  hash: string;
  urlBranchId: string | undefined;
  userBranch: Branch | null | undefined;
}

/**
 * Computes the canonical full path (pathname + search + hash) the signed-in user must
 * be on given their branch. Returns the same path components combined when no
 * redirect is required, otherwise the desired target path.
 */
export const computeBranchEnforcedPath = ({ pathname, search, hash, urlBranchId, userBranch }: ComputeArgs): string => {
  const pathWithoutBranch = stripBranchSegment(pathname, urlBranchId);
  const targetPathname = isDefaultBranch(userBranch)
    ? pathWithoutBranch
    : prependBranchSegment(pathWithoutBranch, (userBranch as Branch).id);
  return `${targetPathname}${search}${hash}`;
};
